/**
 * 印刷エージェント通信サービス
 * ProductListViewとProductRegisterViewで共通利用する印刷エージェント通信機能
 */
const PRINT_AGENT_BASE_URL = process.env.VUE_APP_PRINT_AGENT_URL;
const printAgentService = {
  /**
   * 印刷エージェントの健全性をチェック
   * @param {string} labelSize ラベルサイズ (29 or 62)
   * @param {number} timeout タイムアウト時間（ミリ秒）
   * @returns {Promise<boolean>} 印刷エージェントが利用可能かどうか
   */
  async checkPrintAgentHealth(labelSize) {
    try {
      const controller = new AbortController();
      
      const healthResponse = await fetch(`${PRINT_AGENT_BASE_URL}/health?labelSize=${labelSize}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal
      });
      
      if (!healthResponse.ok) {
        return false;
      }
      
      const healthData = await healthResponse.json();
      return healthData.success === true;
    } catch (error) {
      console.log('印刷エージェントのヘルスチェックに失敗しました:', error.message);
      return false;
    }
  },
  
  /**
   * 印刷リクエストを送信
   * @param {string} pdfBase64 Base64エンコードされたPDFデータ
   * @param {string} labelSize ラベルサイズ (29 or 62)
   * @param {string} jobName 印刷ジョブ名
   * @returns {Promise<Object>} 印刷結果
   */
  async sendPrintRequest(pdfBase64, labelSize, jobName) {
    try {
      const printAgentUrl = `${PRINT_AGENT_BASE_URL}/print`;
      
      const printResponse = await fetch(printAgentUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          labelSize: labelSize,
          pdfBase64: pdfBase64,
          jobName: jobName
        }),
      });
      
      if (!printResponse.ok) {
        throw new Error(`HTTP error! status: ${printResponse.status}`);
      }
      
      return await printResponse.json();
    } catch (error) {
      console.error('印刷リクエスト送信中にエラーが発生しました:', error);
      throw error;
    }
  },
  
  /**
   * ブラウザのプレビュー機能を使用
   * @param {string} pdfBase64 Base64エンコードされたPDFデータ
   * @param {boolean} skipConfirmation 確認ダイアログをスキップするかどうか
   * @returns {Promise<void>}
   */
  async showBrowserPreview(pdfBase64, skipConfirmation = false) {
    try {
      let shouldProceed = skipConfirmation;
      
      if (!skipConfirmation) {
        shouldProceed = confirm('ブラウザでプレビュー表示しますか？');
      }
      
      if (!shouldProceed) {
        return;
      }
      
      const byteCharacters = atob(pdfBase64.replace(/^data:application\/pdf;base64,/, ''));
      const byteNumbers = new Array(byteCharacters.length);
      
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      
      const blobUrl = URL.createObjectURL(blob);
      
      window.open(blobUrl, '_blank', 'noreferrer');
      
    } catch (error) {
      console.error('ブラウザプレビューの表示中にエラーが発生しました:', error);
      alert('ラベルのプレビュー表示に失敗しました');
    }
  },
  
  /**
   * 印刷処理を実行（エージェント印刷またはブラウザプレビュー）
   * @param {string} pdfBase64 Base64エンコードされたPDFデータ
   * @param {string} labelSize ラベルサイズ (29 or 62)
   * @param {string} jobName 印刷ジョブ名
   * @param {boolean} autoPrint 自動印刷かどうか
   * @returns {Promise<boolean>} 印刷が成功したかどうか
   */
  async printLabel(pdfBase64, labelSize, jobName, autoPrint = false) {
    try {
      const agentAvailable = await this.checkPrintAgentHealth(labelSize);
      const errorMessages = [];
      
      if (agentAvailable) {
        const result = await this.sendPrintRequest(pdfBase64, labelSize, jobName);
        
        if(result.success) return true;

        console.error(`印刷エラー: ${result.message}`);
        errorMessages.push('ラベル印刷に失敗しました。');
      } else {
        errorMessages.push(`${labelSize}mmラベル用のプリンターが見つかりません。`);
      }

      errorMessages.push('印刷エージェントまたはプリンターの稼働状況を確認してください。');
      if (!autoPrint) {
        alert(errorMessages.join('\n'));
        await this.showBrowserPreview(pdfBase64);
      }

      return false;
    } catch (error) {
      console.error('印刷処理中にエラーが発生しました:', error);
      if (!autoPrint) {
        await this.showBrowserPreview(pdfBase64);
      }
      return false;
    }
  },
  
  /**
   * 複数ラベルの印刷処理を実行
   * @param {Object} labelSizeToPdfMap ラベルサイズごとのBase64エンコードされたPDFデータのマップ
   * @param {string} jobNamePrefix 印刷ジョブ名のプレフィックス
   * @returns {Promise<boolean>} すべての印刷が成功したかどうか
   */
  async printMultipleLabels(labelSizeToPdfMap, jobNamePrefix) {
    try {
      const printPromises = [];
      const previewPdfs = [];
      const unavailableSizes = [];
      const errorMessages = [];
      
      for (const [labelSize, pdfBase64] of Object.entries(labelSizeToPdfMap)) {
        const agentAvailable = await this.checkPrintAgentHealth(labelSize);
        const jobName = `${jobNamePrefix}-${labelSize}mm`;
        
        if (agentAvailable) {
          printPromises.push(this.sendPrintRequest(pdfBase64, labelSize, jobName));
        } else {
          previewPdfs.push(pdfBase64);
          unavailableSizes.push(labelSize);
        }
      }
      
      if (0 < printPromises.length) {
        const results = await Promise.all(printPromises);
        const failedResults = results.filter(result => !result.success);
        const successfulPrints = results.filter(result => result.success);

        if (failedResults.length === results.length) {
          console.error('印刷に失敗しました:', results);
          errorMessages.push('ラベル印刷に失敗しました。');
        } else if (0 < failedResults.length) {
          console.error('一部の印刷に失敗しました:', failedResults);
          errorMessages.push('一部のラベル印刷に失敗しました。');
        }

        if (0 < successfulPrints.length) {
          alert('ラベル印刷を開始しました。');
        }
      }
      
      if (0 < unavailableSizes.length) {
        errorMessages.push(`${unavailableSizes.join('mm, ')}mmラベル用のプリンターが利用できません。`);
      }
      
      if (0 < errorMessages.length) {
        errorMessages.push('印刷エージェントまたはプリンターの稼働状況を確認してください。');
        alert(errorMessages.join('\n'));
        
        if (0 < previewPdfs.length) {
          const isConfirmed = confirm('印刷に失敗したラベルをブラウザでプレビュー表示しますか？');
          if (isConfirmed) {
            for (const pdfBase64 of previewPdfs) {
              await this.showBrowserPreview(pdfBase64, true);
            }
          }
        }
      }
      
      return 0 < printPromises.length && 0 === previewPdfs.length;
    } catch (error) {
      console.error('印刷処理中にエラーが発生しました:', error);
      
      const isConfirmed = confirm('印刷処理中にエラーが発生しました。ブラウザでプレビュー表示しますか？');
      if (isConfirmed) {
        for (const pdfBase64 of Object.values(labelSizeToPdfMap)) {
          await this.showBrowserPreview(pdfBase64, true);
        }
      }
      
      return false;
    }
  }
};

export default printAgentService;
